import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import type { ReactNode } from 'react'
import { pb } from './pb'
import type { CmsUser } from './types'

interface AuthState {
  user: CmsUser | null
  isAdmin: boolean
  isStaff: boolean
  signIn: (email: string, password: string) => Promise<void>
  signOut: () => void
}

const AuthContext = createContext<AuthState | null>(null)

const currentUser = () =>
  pb.authStore.isValid && pb.authStore.record?.collectionName === 'users'
    ? (pb.authStore.record as unknown as CmsUser)
    : null

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<CmsUser | null>(currentUser)

  useEffect(() => {
    // Fires on sign-in, sign-out, token refresh, and — because PocketBase syncs
    // its auth store through localStorage — in other tabs too.
    return pb.authStore.onChange(() => setUser(currentUser()), true)
  }, [])

  useEffect(() => {
    // A token can outlive the account it belongs to. Refreshing on mount turns
    // a stale token into a clean sign-out instead of a wall of 403s.
    if (!pb.authStore.isValid) return
    pb.collection('users')
      .authRefresh()
      .catch(() => pb.authStore.clear())
  }, [])

  const signIn = useCallback(async (email: string, password: string) => {
    await pb.collection('users').authWithPassword(email, password)
  }, [])

  const signOut = useCallback(() => pb.authStore.clear(), [])

  const value = useMemo<AuthState>(
    () => ({
      user,
      isAdmin: user?.role === 'admin',
      isStaff: user?.role === 'admin' || user?.role === 'editor',
      signIn,
      signOut,
    }),
    [user, signIn, signOut]
  )

  return <AuthContext value={value}>{children}</AuthContext>
}

export function useAuth(): AuthState {
  const context = useContext(AuthContext)
  if (!context) throw new Error('useAuth must be used inside <AuthProvider>')
  return context
}
